import type React from 'react';
import { useState, useRef, type KeyboardEvent, type FocusEvent } from 'react';
import styles from './MultipleInputField.module.scss';

export interface InputItem {
    id: string;
    value: string;
    isValid: boolean;
}

interface ErrorMessages {
    emptyInput?: string;
    invalidValues?: string;
    duplicateValues?: string;
    maxItemsReached?: string;
}

interface MultipleInputFieldProps {
    items: InputItem[];
    onItemsChange: (items: InputItem[]) => void;
    placeholder?: string;
    label?: string;
    description?: string;
    errorMessage?: string;
    onErrorChange?: (error: string) => void;
    validateItem?: (value: string) => boolean;
    separator?: string;
    allowDuplicates?: boolean;
    maxItems?: number;
    maxLength?: number;
    disabled?: boolean;
    className?: string;
    errorMessages?: ErrorMessages;
    formatItem?: (value: string) => string;
}

export const MultipleInputField: React.FC<MultipleInputFieldProps> = ({
    items,
    onItemsChange,
    placeholder = 'Enter values...',
    label,
    description,
    errorMessage,
    onErrorChange,
    validateItem = () => true,
    separator = ',',
    allowDuplicates = false,
    maxItems,
    maxLength,
    disabled = false,
    className = '',
    formatItem,
    errorMessages = {
        emptyInput: 'Please enter a value',
        invalidValues: 'Invalid values',
        duplicateValues: 'Duplicate values',
        maxItemsReached: 'Maximum items reached'
    }
}) => {
    const [currentInput, setCurrentInput] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const handleAddItems = () => {
        const inputText = currentInput.trim();

        if (!inputText) {
            onErrorChange?.(errorMessages.emptyInput || 'Please enter a value');
            return;
        }

        // Check max items limit
        if (maxItems && items.length >= maxItems) {
            onErrorChange?.(errorMessages.maxItemsReached || `Maximum ${maxItems} items allowed`);
            return;
        }

        // Split by separator and clean up
        const valueList = inputText
            .split(separator)
            .map(value => value.trim())
            .filter(value => value);

        if (valueList.length === 0) {
            onErrorChange?.(errorMessages.emptyInput || 'Please enter valid values');
            return;
        }

        const validItems: InputItem[] = [];
        const invalidItems: string[] = [];
        const duplicateItems: string[] = [];

        valueList.forEach(value => {
            if (!validateItem(value)) {
                invalidItems.push(value);
            } else if (!allowDuplicates && items.some(item => item.value.toLowerCase() === value.toLowerCase())) {
                duplicateItems.push(value);
            } else if (!allowDuplicates && validItems.some(item => item.value.toLowerCase() === value.toLowerCase())) {
                // Check for duplicates within the current batch
                duplicateItems.push(value);
            } else {
                validItems.push({
                    id: Date.now().toString() + Math.random(),
                    value: formatItem ? formatItem(value) : value,
                    isValid: true
                });
            }
        });

        if (invalidItems.length > 0) {
            const baseMessage = errorMessages.invalidValues || 'Invalid values';
            onErrorChange?.(`${baseMessage}: ${invalidItems.join(', ')}`);
            return;
        }

        if (duplicateItems.length > 0) {
            const baseMessage = errorMessages.duplicateValues || 'Duplicate values';
            onErrorChange?.(`${baseMessage}: ${duplicateItems.join(', ')}`);
            return;
        }

        // Check if adding these items would exceed max
        if (maxItems && items.length + validItems.length > maxItems) {
            onErrorChange?.(
                errorMessages.maxItemsReached ||
                    `Cannot add ${validItems.length} items. Maximum ${maxItems} items allowed`
            );
            return;
        }

        onItemsChange([...items, ...validItems]);
        setCurrentInput('');
        onErrorChange?.('');
    };

    const handleRemoveItem = (id: string) => {
        onItemsChange(items.filter(item => item.id !== id));
        onErrorChange?.('');
    };

    const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddItems();
        } else if (e.key === 'Escape') {
            setCurrentInput('');
            onErrorChange?.('');
            handleAddItems();
        } else if (e.key === 'Backspace' && !currentInput && items.length > 0) {
            // Remove last item when backspace is pressed on empty input
            handleRemoveItem(items[items.length - 1].id);
        }
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
        if (!currentInput.trim()) {
            onErrorChange?.('');
        } else {
            handleAddItems();
        }
    };

    const handleContainerClick = () => {
        inputRef.current?.focus();
    };

    return (
        <div className={`${styles.wrapper} ${className}`}>
            {label && <h3 className={styles.label}>{label}</h3>}
            {description && <p className={styles.description}>{description}</p>}

            <div
                className={`${styles.inputContainer} ${disabled ? styles.disabled : ''} ${
                    errorMessage ? styles.error : ''
                }`}
                onClick={handleContainerClick}
            >
                {items.map(item => (
                    <div
                        key={item.id}
                        className={styles.tag}
                    >
                        <span className={styles.tagText}>{item.value}</span>
                        <button
                            className={styles.tagRemove}
                            onClick={e => {
                                e.stopPropagation();
                                handleRemoveItem(item.id);
                            }}
                            type="button"
                            aria-label="Remove item"
                            disabled={disabled}
                        >
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M9 3L3 9M3 3L9 9"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </div>
                ))}

                <input
                    ref={inputRef}
                    type="text"
                    value={currentInput}
                    onChange={e => {
                        setCurrentInput(e.target.value);
                        onErrorChange?.('');
                    }}
                    onKeyDown={handleKeyPress}
                    onBlur={handleBlur}
                    placeholder={items.length === 0 ? placeholder : ''}
                    className={styles.hiddenInput}
                    disabled={disabled}
                    maxLength={maxLength}
                />
            </div>

            {errorMessage && (
                <div className={styles.errorMessage}>
                    <span>{errorMessage}</span>
                </div>
            )}
        </div>
    );
};
